import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useSocketEvent } from '../hooks/useSocket';
import api from '../services/api';
import toast from 'react-hot-toast';
const STATUS_FLOW = ['PENDIENTE','EN_PRODUCCION','REVISION_INTERNA','REVISION_CLIENTE','APROBADO','SUBIDO'];
const STATUS_LABELS: any = { PENDIENTE:'Pendiente', EN_PRODUCCION:'En producción', REVISION_INTERNA:'Revisión interna', REVISION_CLIENTE:'Revisión cliente', APROBADO:'Aprobado', SUBIDO:'Subido', CANCELADO:'Cancelado' };
const STATUS_COLORS: any = { PENDIENTE:'#64748b', EN_PRODUCCION:'#2688c9', REVISION_INTERNA:'#8b5cf6', REVISION_CLIENTE:'#f59e0b', APROBADO:'#10b981', SUBIDO:'#2688c9', CANCELADO:'#ef4444' };
const TYPE_LABELS: any = { PARRILLA_GESTIONADA:'Parrilla gestionada', PARRILLA_CLIENTE:'Parrilla cliente', DISENO_ESPORADICO:'Diseño esporádico' };
const NETWORKS = ['Instagram','Facebook','TikTok','LinkedIn','Twitter','YouTube','WhatsApp'];
const FILE_ICONS: any = { image:'🖼', video:'🎬', pdf:'📄', default:'📎' };
const getFileType = (url: string) => {
  const ext = url.split('.').pop()?.toLowerCase()||'';
  if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return 'image';
  if (['mp4','mov','avi','webm'].includes(ext)) return 'video';
  if (ext==='pdf') return 'pdf';
  return 'default';
};
const formatSize = (bytes: number) => bytes>1024*1024 ? `${(bytes/1024/1024).toFixed(1)} MB` : `${(bytes/1024).toFixed(0)} KB`;
const Requests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [pieces, setPieces] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'comentarios'|'archivos'>('comentarios');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({ title:'', type:'PARRILLA_GESTIONADA', pieceType:'ESTATICA', networks:[] as string[], description:'', references:'', sourceLink:'', notes:'', brandId:'', dueDate:'', assignedToId:'', reviewerId:'' });
  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [rr, br] = await Promise.all([api.get('/requests'), api.get('/brands')]);
      setRequests(Array.isArray(rr.data)?rr.data:[]);
      setBrands(Array.isArray(br.data)?br.data:[]);
      const ur = await api.get('/users'); setUsers(Array.isArray(ur.data)?ur.data:[]);
    } catch {} finally { setLoading(false); }
  };
  useSocketEvent('refresh:requests', load);
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.brandId) { toast.error('Completa los campos requeridos'); return; }
    try {
      await api.post('/requests', form);
      toast.success('Solicitud creada');
      setShowModal(false);
      setForm({ title:'', type:'PARRILLA_GESTIONADA', pieceType:'ESTATICA', networks:[], description:'', references:'', sourceLink:'', notes:'', brandId:'', dueDate:'', assignedToId:'', reviewerId:'' });
      load();
    } catch (e: any) { toast.error(e.response?.data?.error||'Error'); }
  };
  const handleStatusChange = async (id: string, status: string) => {
    try {
      await api.put(`/requests/${id}/status`, { status });
      toast.success('Estado actualizado');
      setSelected((p: any) => p?.id===id ? {...p, status} : p);
      load();
    } catch { toast.error('Error'); }
  };
  const handleAssign = async (id: string, userId: string) => {
    try { await api.put(`/requests/${id}/assign`, { userId }); toast.success('Asignado'); load(); }
    catch { toast.error('Error'); }
  };
  const openDetail = async (r: any) => {
    setSelected(r); setActiveTab('comentarios');
    try {
      const [cr, pr] = await Promise.all([api.get(`/requests/${r.id}/comments`), api.get(`/pieces/request/${r.id}`)]);
      setComments(Array.isArray(cr.data)?cr.data:[]);
      setPieces(Array.isArray(pr.data)?pr.data:[]);
    } catch {}
  };
  const sendComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/requests/${selected.id}/comment`, { content: newComment, step: selected.status });
      setNewComment('');
      const cr = await api.get(`/requests/${selected.id}/comments`);
      setComments(Array.isArray(cr.data)?cr.data:[]);
    } catch { toast.error('Error'); }
  };
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/pieces', { title: file.name, fileUrl: uploadRes.data.url, brandId: selected.brandId, requestId: selected.id });
      toast.success('Archivo subido');
      const pr = await api.get(`/pieces/request/${selected.id}`);
      setPieces(Array.isArray(pr.data)?pr.data:[]);
    } catch (e: any) { toast.error('Error al subir archivo'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const nextStatus = selected ? STATUS_FLOW[STATUS_FLOW.indexOf(selected.status)+1] : null;
  const canAdvance = user?.role !== 'CLIENTE' || ['REVISION_CLIENTE','APROBADO'].includes(selected?.status||'');
  const inp = { width:'100%', padding:'10px 12px', borderRadius:'8px', border:'1px solid rgba(15,42,74,0.12)', background:'#f8fafc', color:'#0f2a4a', fontSize:'13px', outline:'none', fontFamily:'"Montserrat",sans-serif', boxSizing:'border-box' as const };
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(15,42,74,0.45)', textTransform:'uppercase' as const, letterSpacing:'0.8px', marginBottom:'5px' };
  return (
    <div className="zoik-page-container" style={{display:'flex',minHeight:'100vh',background:'#f8fafc',fontFamily:'"Montserrat",sans-serif'}}>
      <Sidebar/>
      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        padding: '36px 40px',
        overflowY: 'auto',
        overflowX: 'hidden'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'28px',flexWrap:'wrap',gap:'12px'}}>
          <div>
            <h1 style={{margin:0,fontSize:'26px',fontWeight:800,color:'#0f2a4a'}}>Solicitudes</h1>
            <p style={{margin:'6px 0 0',fontSize:'14px',color:'rgba(15,42,74,0.45)',fontWeight:500}}>Gestión de briefs y producción</p>
          </div>
          {user?.role !== 'DISENO' && (
            <button onClick={()=>setShowModal(true)} style={{padding:'10px 20px',borderRadius:'9px',border:'none',background:'linear-gradient(135deg,#1e6fa8,#2688c9)',color:'white',fontWeight:700,fontSize:'13px',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',boxShadow:'0 2px 6px rgba(15, 42, 74, 0.08)'}}>
              + Nueva solicitud
            </button>
          )}
        </div>
        <div className="zoik-touch-scroll" style={{background:'#ffffff',borderRadius:'14px',border:'1px solid rgba(15,42,74,0.07)',overflowX:'auto',boxShadow:'0 1px 4px rgba(15,42,74,0.04)'}}>
          <table style={{width:'100%',minWidth:'650px',borderCollapse:'collapse',fontSize:'13px'}}>
            <thead>
              <tr style={{background:'#f8fafc'}}>
                {['Título','Marca','Tipo','Estado','Asignado','Fecha',''].map(h=>(
                  <th key={h} style={{padding:'12px 16px',textAlign:'left',fontSize:'11px',fontWeight:700,color:'rgba(15,42,74,0.45)',textTransform:'uppercase',letterSpacing:'0.6px',borderBottom:'1px solid rgba(15,42,74,0.07)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{padding:'48px',textAlign:'center',color:'rgba(15,42,74,0.25)',fontSize:'14px'}}>Cargando...</td></tr>
              ) : requests.length===0 ? (
                <tr><td colSpan={7} style={{padding:'48px',textAlign:'center',color:'rgba(15,42,74,0.25)',fontSize:'14px'}}>No hay solicitudes aún</td></tr>
              ) : requests.map(r=>(
                <tr key={r.id} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)',fontWeight:700,color:'#0f2a4a',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.title}</td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)',color:'rgba(15,42,74,0.6)',fontWeight:500}}>{r.brand?.name}</td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)',color:'rgba(15,42,74,0.45)',fontSize:'12px'}}>{TYPE_LABELS[r.type]}</td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)'}}>
                    <span style={{background:`${STATUS_COLORS[r.status]}15`,color:STATUS_COLORS[r.status],padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:700,whiteSpace:'nowrap'}}>{STATUS_LABELS[r.status]}</span>
                  </td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)',color:'rgba(15,42,74,0.5)',fontSize:'12px'}}>{r.assignedTo?`${r.assignedTo.firstName} ${r.assignedTo.lastName}`:'—'}</td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)',color:'rgba(15,42,74,0.45)',fontSize:'12px',whiteSpace:'nowrap'}}>{r.dueDate?new Date(r.dueDate).toLocaleDateString('es-CO'):'—'}</td>
                  <td style={{padding:'14px 16px',borderBottom:'1px solid rgba(15,42,74,0.05)'}}>
                    <button onClick={()=>openDetail(r)} style={{padding:'5px 12px',borderRadius:'7px',border:'1px solid rgba(15,42,74,0.12)',background:'transparent',color:'#2688c9',fontSize:'12px',fontWeight:700,cursor:'pointer',fontFamily:'"Montserrat",sans-serif'}}>Ver</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {showModal && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(15,42,74,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:'#ffffff',borderRadius:'16px',padding:'32px',width:'90%',maxWidth:'600px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(15,42,74,0.15)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
                <h2 style={{margin:0,fontSize:'18px',fontWeight:800,color:'#0f2a4a'}}>Nueva solicitud</h2>
                <button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',color:'rgba(15,42,74,0.35)',cursor:'pointer',fontSize:'20px',lineHeight:1}}>×</button>
              </div>
              <form onSubmit={handleCreate}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px',marginBottom:'16px'}}>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={lbl}>Título *</label>
                    <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Ej: Post Instagram Mayo semana 1" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>Marca *</label>
                    <select value={form.brandId} onChange={e=>setForm(p=>({...p,brandId:e.target.value}))} style={inp}>
                      <option value="">Seleccionar marca</option>
                      {brands.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Tipo de servicio</label>
                    <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={inp}>
                      <option value="PARRILLA_GESTIONADA">Parrilla gestionada</option>
                      <option value="PARRILLA_CLIENTE">Parrilla cliente</option>
                      <option value="DISENO_ESPORADICO">Diseño esporádico</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Tipo de pieza</label>
                    <select value={form.pieceType} onChange={e=>setForm(p=>({...p,pieceType:e.target.value}))} style={inp}>
                      <option value="ESTATICA">Estática</option>
                      <option value="ANIMACION">Animación</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Fecha de entrega</label>
                    <input type="date" value={form.dueDate} onChange={e=>setForm(p=>({...p,dueDate:e.target.value}))} style={inp}/>
                  </div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={lbl}>Redes sociales</label>
                    <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginTop:'4px'}}>
                      {NETWORKS.map(n=>(
                        <label key={n} style={{display:'flex',alignItems:'center',gap:'5px',cursor:'pointer',fontSize:'13px',color:form.networks.includes(n)?'#2688c9':'rgba(15,42,74,0.5)',fontWeight:form.networks.includes(n)?700:400}}>
                          <input type="checkbox" checked={form.networks.includes(n)} onChange={e=>setForm(p=>({...p,networks:e.target.checked?[...p.networks,n]:p.networks.filter(x=>x!==n)}))} style={{accentColor:'#2688c9'}}/>
                          {n}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={lbl}>Brief</label>
                    <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Describe el contenido, objetivo, tono..." style={{...inp,resize:'vertical' as const}}/>
                  </div>
                  <div>
                    <label style={lbl}>Referencias</label>
                    <input value={form.references} onChange={e=>setForm(p=>({...p,references:e.target.value}))} placeholder="Links o descripción" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>Fuente / Link</label>
                    <input value={form.sourceLink} onChange={e=>setForm(p=>({...p,sourceLink:e.target.value}))} placeholder="https://" style={inp}/>
                  </div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={lbl}>Notas internas</label>
                    <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2} style={{...inp,resize:'vertical' as const}}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
                    <div>
                      <label style={lbl}>RESPONSABLE</label>
                      <select value={form.assignedToId} onChange={e=>setForm(p=>({...p,assignedToId:e.target.value}))} style={inp}>
                        <option value="">Sin asignar</option>
                        {users.filter(u=>u.role!=='CLIENTE').map((u:any)=><option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>REVISOR</label>
                      <select value={form.reviewerId} onChange={e=>setForm(p=>({...p,reviewerId:e.target.value}))} style={inp}>
                        <option value="">Sin asignar</option>
                        {users.filter(u=>u.role!=='CLIENTE').map((u:any)=><option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                      </select>
                    </div>
                  </div>

                  </div>
                <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
                  <button type="button" onClick={()=>setShowModal(false)} style={{padding:'10px 18px',borderRadius:'8px',border:'1px solid rgba(15,42,74,0.12)',background:'transparent',color:'rgba(15,42,74,0.5)',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',fontSize:'13px',fontWeight:600}}>Cancelar</button>
                  <button type="submit" style={{padding:'10px 18px',borderRadius:'8px',border:'none',background:'linear-gradient(135deg,#1e6fa8,#2688c9)',color:'white',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',fontSize:'13px',fontWeight:700}}>Crear solicitud</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {selected && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(15,42,74,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:'#ffffff',borderRadius:'16px',padding:'32px',width:'90%',maxWidth:'700px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(15,42,74,0.15)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'20px'}}>
                <div>
                  <h2 style={{margin:0,fontSize:'18px',fontWeight:800,color:'#0f2a4a'}}>{selected.title}</h2>
                  <div style={{fontSize:'13px',color:'rgba(15,42,74,0.45)',marginTop:'4px',fontWeight:500}}>{selected.brand?.name} · {TYPE_LABELS[selected.type]}</div>
                </div>
                <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:'rgba(15,42,74,0.35)',cursor:'pointer',fontSize:'20px',lineHeight:1}}>×</button>
              </div>
              <div style={{marginBottom:'20px',overflowX:'auto'}}>
                <div style={{display:'flex',gap:'4px',minWidth:'max-content'}}>
                  {STATUS_FLOW.map((s,i)=>{
                    const idx=STATUS_FLOW.indexOf(selected.status); const done=i<idx; const current=i===idx;
                    return (
                      <div key={s} style={{display:'flex',alignItems:'center'}}>
                        <div style={{padding:'5px 10px',borderRadius:'6px',fontSize:'10px',fontWeight:700,background:current?`${STATUS_COLORS[s]}15`:done?'rgba(16,185,129,0.08)':'rgba(15,42,74,0.04)',color:current?STATUS_COLORS[s]:done?'#10b981':'rgba(15,42,74,0.3)',border:`1px solid ${current?STATUS_COLORS[s]+'40':done?'rgba(16,185,129,0.2)':'rgba(15,42,74,0.08)'}`,whiteSpace:'nowrap'}}>
                          {done?'✓ ':''}{STATUS_LABELS[s]}
                        </div>
                        {i<STATUS_FLOW.length-1&&<div style={{width:'12px',height:'1px',background:'rgba(15,42,74,0.1)',margin:'0 2px'}}/>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {user?.role !== 'CLIENTE' && (
                <div style={{marginBottom:'16px'}}>
                  <label style={lbl}>Asignar a</label>
                  <select value={selected.assignedToId||''} onChange={e=>handleAssign(selected.id,e.target.value)} style={inp}>
                    <option value="">Sin asignar</option>
                    {users.filter((u:any)=>u.role!=='CLIENTE').map((u:any)=><option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>)}
                  </select>
                </div>
              )}
              {nextStatus && canAdvance && selected.status!=='SUBIDO' && (
                <button onClick={()=>handleStatusChange(selected.id,nextStatus)} style={{width:'100%',padding:'11px',borderRadius:'9px',border:'none',background:'linear-gradient(135deg,#1e6fa8,#2688c9)',color:'white',fontWeight:700,fontSize:'13px',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',marginBottom:'20px',boxShadow:'0 2px 6px rgba(15, 42, 74, 0.08)'}}>
                  Avanzar → {STATUS_LABELS[nextStatus]}
                </button>
              )}
              <div style={{display:'flex',borderBottom:'1px solid rgba(15,42,74,0.07)',marginBottom:'16px'}}>
                {[['comentarios',`Comentarios (${comments.length})`],['archivos',`Archivos (${pieces.length})`]].map(([k,l])=>(
                  <button key={k} onClick={()=>setActiveTab(k as any)} style={{padding:'9px 16px',border:'none',borderBottom:activeTab===k?'2px solid #2688c9':'2px solid transparent',background:'transparent',color:activeTab===k?'#0f2a4a':'rgba(15,42,74,0.4)',fontWeight:activeTab===k?700:500,fontSize:'13px',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',marginBottom:'-1px'}}>
                    {l}
                  </button>
                ))}
              </div>
              {activeTab==='comentarios'&&(
                <div>
                  <div style={{maxHeight:'200px',overflowY:'auto',marginBottom:'12px'}}>
                    {comments.length===0&&<div style={{fontSize:'13px',color:'rgba(15,42,74,0.25)',padding:'12px 0'}}>Sin comentarios</div>}
                    {comments.map(c=>(
                      <div key={c.id} style={{marginBottom:'10px',background:'#f8fafc',borderRadius:'8px',padding:'10px 12px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                          <span style={{fontSize:'12px',fontWeight:700,color:'#2688c9'}}>{c.user?.firstName} {c.user?.lastName}</span>
                          <span style={{fontSize:'11px',color:'rgba(15,42,74,0.3)'}}>{new Date(c.createdAt).toLocaleString('es-CO')}</span>
                        </div>
                        <div style={{fontSize:'13px',color:'rgba(15,42,74,0.7)'}}>{c.content}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:'8px'}}>
                    <input value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Escribe un comentario..." onKeyDown={e=>e.key==='Enter'&&sendComment()} style={{...inp,flex:1}}/>
                    <button onClick={sendComment} style={{padding:'10px 16px',borderRadius:'8px',border:'none',background:'linear-gradient(135deg,#1e6fa8,#2688c9)',color:'white',cursor:'pointer',fontWeight:700,fontSize:'13px',fontFamily:'"Montserrat",sans-serif',whiteSpace:'nowrap'}}>Enviar</button>
                  </div>
                </div>
              )}
              {activeTab==='archivos'&&(
                <div>
                  <input ref={fileInputRef} type="file" onChange={handleUpload} style={{display:'none'}} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.ai,.psd"/>
                  <button onClick={()=>fileInputRef.current?.click()} disabled={uploading}
                    style={{width:'100%',padding:'11px',borderRadius:'9px',border:'2px dashed rgba(38,136,201,0.3)',background:'rgba(38,136,201,0.03)',color:'#2688c9',fontWeight:700,fontSize:'13px',cursor:'pointer',fontFamily:'"Montserrat",sans-serif',marginBottom:'16px',opacity:uploading?0.6:1}}>
                    {uploading?'Subiendo...' : '+ Subir archivo'}
                  </button>
                  {pieces.length===0&&<div style={{fontSize:'13px',color:'rgba(15,42,74,0.25)',padding:'12px 0'}}>Sin archivos adjuntos</div>}
                  <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    {pieces.map(p=>{
                      const ft = p.fileUrl ? getFileType(p.fileUrl) : 'default';
                      return (
                        <div key={p.id} style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px 14px',background:'#f8fafc',borderRadius:'8px',border:'1px solid rgba(15,42,74,0.06)'}}>
                          <span style={{fontSize:'20px',flexShrink:0}}>{FILE_ICONS[ft]}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:'13px',fontWeight:600,color:'#0f2a4a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.title}</div>
                            <div style={{fontSize:'11px',color:'rgba(15,42,74,0.35)',marginTop:'1px'}}>{new Date(p.createdAt).toLocaleDateString('es-CO')}</div>
                          </div>
                          {p.fileUrl&&(
                            <a href={p.fileUrl} target="_blank" rel="noreferrer" style={{padding:'5px 12px',borderRadius:'6px',border:'1px solid rgba(15,42,74,0.12)',background:'transparent',color:'#2688c9',fontSize:'12px',fontWeight:600,textDecoration:'none',whiteSpace:'nowrap',flexShrink:0}}>Ver</a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
export default Requests;
